import {
	AttachmentFile,
	bankInformationFileTypeDef,
	companyBackgroundFileTypeDef,
	corporateFinancialsFileTypeDef,
	type FileId,
	FileType,
	personalFinancialsFileTypeDef,
	type SuretyAccountId,
	type UploadedFile,
	wipFileTypeDef,
} from "@inrev/common";
import { accountAttachmentsQueryKey, useFetchAccountAttachments } from "@portal/api/account";
import { useAccountAttachmentActions } from "@portal/api/attachments";
import {
	applyClassificationUpdate,
	attachmentFileClassificationAccessor,
	pendingClassificationFileIds,
	useClassificationWatchers,
} from "@portal/api/files";
import { useExtractFinancialFile } from "@portal/api/financial-extraction/admin";
import { useFetchFinancialExtractions } from "@portal/api/financials";
import { useFetchWipExtractions } from "@portal/api/wip";
import { useAdminExtractWipFile } from "@portal/api/wip/admin";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ReportedRetryableLoadError } from "../features/load-errors";
import { PendingExtractionsBanner } from "../financials/PendingExtractionsBanner";
import { Card, Text } from "../primitives";
import { Dropzone } from "../ui/Dropzone";
import { DynamicDataTable } from "../ui/DynamicDataTable/DynamicDataTable";
import { CategoryFilterOption, CategoryFilters } from "./CategoryFilters";
import { DownloadAllAttachments } from "./DownloadAllAttachments";
import { FinancialExtractionDeleteDialogContent } from "./FinancialExtractionDeleteDialogContent";
import { PendingUploads } from "./PendingUploads";
import { UploadedDocumentCategory, UploadedDocumentFilter, UploadedDocumentRow } from "./types";
import {
	createFinancialsDocumentsColumns,
	createUploadedDocumentsColumns,
	createWipDocumentsColumns,
	UPLOADED_DOCUMENTS_INITIAL_SORTING,
} from "./UploadedDocumentsColumns";
import { mapAttachmentsToUploadedDocumentsRow } from "./utils/mapAttachmentsToUploadedDocumentsRow";
import { WipExtractionDeleteDialogContent } from "./WipExtractionDeleteDialogContent";

const corporateFinancialsTypes = new Set<FileType>(corporateFinancialsFileTypeDef);
const personalFinancialsTypes = new Set<FileType>(personalFinancialsFileTypeDef);
const wipTypes = new Set<FileType>(wipFileTypeDef);
const companyBackgroundTypes = new Set<FileType>(companyBackgroundFileTypeDef);
const bankInformationTypes = new Set<FileType>(bankInformationFileTypeDef);

export function uploadedDocumentCategories(attachment: AttachmentFile): UploadedDocumentCategory[] {
	const types = attachment.file.types ?? [];
	const categorySet = new Set<UploadedDocumentCategory>();

	for (const t of types) {
		if (corporateFinancialsTypes.has(t)) categorySet.add("financials");
		if (personalFinancialsTypes.has(t)) categorySet.add("personal_financials");
		if (wipTypes.has(t)) categorySet.add("work_in_progress");
		if (companyBackgroundTypes.has(t)) categorySet.add("company");
		if (bankInformationTypes.has(t)) categorySet.add("banking");
	}

	const categories: UploadedDocumentCategory[] =
		categorySet.size > 0 ? Array.from(categorySet) : ["other"];
	return categories;
}

export type UploadedDocumentsProps = {
	accountId: SuretyAccountId;
	filter?: (attachment: AttachmentFile) => boolean;
	downloadFileName?: string;
	allowDelete?: boolean;
	allowUpload?: boolean;
	useCardLayout?: boolean;
	testId?: string;
};

const FILTER_OPTIONS: CategoryFilterOption<UploadedDocumentFilter> = [
	{ id: "all", label: "All" },
	{ id: "company", label: "Company" },
	{ id: "financials", label: "Financials" },
	{ id: "work_in_progress", label: "Work in Progress" },
	{ id: "banking", label: "Banking" },
	{ id: "personal_financials", label: "Personal Financials" },
	{ id: "other", label: "Other" },
];

function attachmentMatchesCategoryFilter(
	attachment: AttachmentFile,
	filter: UploadedDocumentFilter,
): boolean {
	if (filter === "all") return true;
	return uploadedDocumentCategories(attachment).includes(filter);
}

export const UploadedDocuments: FC<UploadedDocumentsProps> = ({
	accountId,
	filter,
	downloadFileName,
	allowDelete,
	allowUpload = true,
	useCardLayout = false,
	testId = "admin-account-documents-card",
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const { showErrorFlag } = useFlags();
	const [categoryFilter, setCategoryFilter] = useState<UploadedDocumentFilter>("all");
	const isFinancialsLayout = categoryFilter === "financials";
	const isWipLayout = categoryFilter === "work_in_progress";

	const actionsConfig = useAccountAttachmentActions({
		accountId,
		isAdmin,
		allowDelete,
		allowUpload,
	});

	const {
		handleUpload,
		filesPendingUpload,
		filesCompletedUpload,
		downloadBaseUrl,
		downloadQueryKeyPrefix,
	} = actionsConfig;

	const {
		extractFinancialFile,
		extractFinancialFilePending,
		extractFinancialFileError,
		dismissExtractFinancialFileError,
	} = useExtractFinancialFile(accountId);

	const { extractWipFile, extractWipFilePending, extractWipFileError, dismissExtractWipFileError } =
		useAdminExtractWipFile(accountId);

	useEffect(() => {
		if (extractFinancialFileError) {
			showErrorFlag({
				heading: "Extraction Error",
				message: extractFinancialFileError.message,
				onDismiss: dismissExtractFinancialFileError,
			});
		}
		if (extractWipFileError) {
			showErrorFlag({
				heading: "WIP Extraction Error",
				message: extractWipFileError.message,
				onDismiss: dismissExtractWipFileError,
			});
		}
	}, [
		showErrorFlag,
		extractFinancialFileError,
		dismissExtractFinancialFileError,
		extractWipFileError,
		dismissExtractWipFileError,
	]);

	const { attachments, attachmentsIsLoading, attachmentsError, refetchAccountAttachments } =
		useFetchAccountAttachments(accountId);

	const queryClient = useQueryClient();
	const pendingClassificationIds = useMemo(
		() => pendingClassificationFileIds(attachments, attachmentFileClassificationAccessor),
		[attachments],
	);
	const handleClassificationUpdate = useCallback(
		(fileId: FileId, updatedFile: UploadedFile) => {
			queryClient.setQueryData(accountAttachmentsQueryKey(accountId), (prev?: AttachmentFile[]) => {
				if (!prev) return prev;
				return (
					applyClassificationUpdate(
						prev,
						fileId,
						updatedFile,
						attachmentFileClassificationAccessor,
					) ?? prev
				);
			});
		},
		[accountId, queryClient],
	);
	const classificationWatchers = useClassificationWatchers(
		pendingClassificationIds,
		handleClassificationUpdate,
	);
	const {
		financialExtractions,
		financialExtractionsLoading,
		financialExtractionsError,
		refetchFinancialExtractions,
	} = useFetchFinancialExtractions(accountId);
	const { wipExtractions, wipExtractionsLoading, wipExtractionsError, refetchWipExtractions } =
		useFetchWipExtractions(accountId);

	const retryDocuments = useCallback(() => {
		void refetchAccountAttachments();
		void refetchFinancialExtractions();
		void refetchWipExtractions();
	}, [refetchAccountAttachments, refetchFinancialExtractions, refetchWipExtractions]);

	const downloadConfig = useMemo(
		() => ({
			baseUrl: downloadBaseUrl,
			queryKeyPrefix: downloadQueryKeyPrefix,
			fileName: downloadFileName,
		}),
		[downloadBaseUrl, downloadQueryKeyPrefix, downloadFileName],
	);

	const buildAttachmentDeleteConfirmation = useCallback(
		(row: UploadedDocumentRow) => {
			if (!isAdmin) return null;
			if (row.financialExtraction) {
				return <FinancialExtractionDeleteDialogContent row={row} />;
			}
			if (row.wipExtraction) {
				return <WipExtractionDeleteDialogContent row={row} />;
			}
			return null;
		},
		[isAdmin],
	);

	const enrichedActionsConfig = useMemo(
		() => ({
			...actionsConfig,
			buildAttachmentDeleteConfirmation,
			analyticsContext: "account-documents" as const,
		}),
		[actionsConfig, buildAttachmentDeleteConfirmation],
	);

	const genericColumns = useMemo(
		() => createUploadedDocumentsColumns({ actions: enrichedActionsConfig }),
		[enrichedActionsConfig],
	);

	const financialRetryConfig = useMemo(
		() => ({
			onTriggerExtraction: (row: UploadedDocumentRow) => extractFinancialFile(row.file.id),
			extractionPending: extractFinancialFilePending,
		}),
		[extractFinancialFile, extractFinancialFilePending],
	);

	const wipRetryConfig = useMemo(
		() => ({
			onTriggerExtraction: (row: UploadedDocumentRow) => extractWipFile(row.file.id),
			extractionPending: extractWipFilePending,
		}),
		[extractWipFile, extractWipFilePending],
	);

	const financialsColumns = useMemo(
		() =>
			createFinancialsDocumentsColumns(
				enrichedActionsConfig,
				isAdmin,
				isAdmin ? financialRetryConfig : undefined,
			),
		[enrichedActionsConfig, isAdmin, financialRetryConfig],
	);

	const wipColumns = useMemo(
		() =>
			createWipDocumentsColumns(
				enrichedActionsConfig,
				isAdmin,
				isAdmin ? wipRetryConfig : undefined,
			),
		[enrichedActionsConfig, isAdmin, wipRetryConfig],
	);

	const tableLayout = useMemo(() => {
		if (isFinancialsLayout) {
			return {
				name: "uploaded_docs_financials_v2",
				columns: financialsColumns,
				isLoading: attachmentsIsLoading || financialExtractionsLoading,
				error: attachmentsError ?? financialExtractionsError,
			};
		}
		if (isWipLayout) {
			return {
				name: "uploaded_docs_wip_v1",
				columns: wipColumns,
				isLoading: attachmentsIsLoading || wipExtractionsLoading,
				error: attachmentsError ?? wipExtractionsError,
			};
		}
		return {
			name: "uploaded_docs_generic_v2",
			columns: genericColumns,
			isLoading: attachmentsIsLoading || financialExtractionsLoading || wipExtractionsLoading,
			error: attachmentsError,
		};
	}, [
		isFinancialsLayout,
		isWipLayout,
		financialsColumns,
		wipColumns,
		genericColumns,
		attachmentsIsLoading,
		financialExtractionsLoading,
		wipExtractionsLoading,
		attachmentsError,
		financialExtractionsError,
		wipExtractionsError,
	]);

	const error: Error | null =
		tableLayout.error instanceof Error
			? tableLayout.error
			: tableLayout.error != null
				? new Error(String(tableLayout.error))
				: null;

	const filteredAttachments = useMemo(
		() => (filter ? attachments.filter(filter) : attachments),
		[attachments, filter],
	);

	const allUploadedRows = useMemo(
		() =>
			mapAttachmentsToUploadedDocumentsRow(
				filteredAttachments,
				financialExtractions ?? [],
				[],
				wipExtractions ?? [],
			),
		[filteredAttachments, financialExtractions, wipExtractions],
	);

	const tableData = useMemo((): UploadedDocumentRow[] => {
		return categoryFilter === "all"
			? allUploadedRows
			: allUploadedRows.filter((row) => attachmentMatchesCategoryFilter(row, categoryFilter));
	}, [categoryFilter, allUploadedRows]);

	const showDownloadAll = downloadConfig && allUploadedRows.length > 0;
	const downloadAll = showDownloadAll && (
		<DownloadAllAttachments
			downloadConfig={downloadConfig}
			allUploadedRows={allUploadedRows}
			analyticsContext="account-documents"
		/>
	);
	const dropzone = allowUpload && (
		<Dropzone onSuccess={handleUpload} trackingId="account-documents-attachment-dropzone" />
	);
	const pendingUploads = (
		<PendingUploads
			pendingUploadFiles={filesPendingUpload}
			completedUploadFiles={filesCompletedUpload}
		/>
	);

	const table = error ? (
		<ReportedRetryableLoadError error={error} resource="documents" onRetry={retryDocuments} />
	) : (
		<DynamicDataTable
			name={tableLayout.name}
			columns={tableLayout.columns}
			data={tableLayout.isLoading ? undefined : tableData}
			loadingMessage="Loading documents..."
			minHeight={400}
			maxHeight={600}
			initialSorting={UPLOADED_DOCUMENTS_INITIAL_SORTING}
		/>
	);
	const categoryFilters = (
		<CategoryFilters<UploadedDocumentFilter>
			filterOptions={FILTER_OPTIONS}
			categoryFilter={categoryFilter}
			setCategoryFilter={setCategoryFilter}
		/>
	);

	if (useCardLayout) {
		return (
			<Card elevation="raised" className="rounded-xl" id="attachments" data-testid={testId}>
				<Card.Body className="p-6">
					<div className="flex flex-col w-full gap-4">
						{classificationWatchers}
						<div className="flex items-center justify-between w-full">
							<Text type="title" color="primary">
								Uploaded Documents
							</Text>
							{downloadAll}
						</div>
						<PendingExtractionsBanner accountId={accountId} />
						{dropzone}
						{pendingUploads}
						{categoryFilters}
						{table}
					</div>
				</Card.Body>
			</Card>
		);
	}

	return (
		<div id="attachments" className="flex flex-col gap-4">
			{classificationWatchers}
			<div className="flex flex-col gap-4 justify-center px-2">
				<Text type="heading" color="primary" className="flex-1">
					Uploaded Documents
				</Text>

				<PendingExtractionsBanner accountId={accountId} />
				{dropzone}
				{pendingUploads}
				<div className="flex justify-between items-end">
					{categoryFilters}
					{downloadAll}
				</div>
			</div>

			{table}
		</div>
	);
};
